<script setup lang="ts">
import { getFullName, parseFullName, toApiDate } from '@/utils/formatters'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify/framework'
import { VIconBtn } from 'vuetify/labs/VIconBtn'

import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useAuditGroupStore } from '@/modules/audits/stores/useAuditGroupStore/useAuditGroupStore'
import { useAuditForm } from '@/modules/audits/stores/useAuditRegisterStore/composables/useAuditForm'
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

import CollapsibleCell from '@/components/createdUi/CollapsibleCell.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import DateMaskInput from '@/components/createdUi/DateMaskInput.vue'
import EmployeeProfileSidebar from '@/components/createdUi/EmployeeProfileSidebar.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import NewTreeMultiSelectComponent from '@/components/createdUi/NewTreeMultiSelectComponent.vue'
import { searchDepartments } from '@/components/createdUi/treeSearch'
import SearchableSelectList from '@/components/createdUi/SearchableSelectList.vue'
import UniversalTreeMultiSelect from '@/components/createdUi/UniversalTreeMultiSelect.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import type { IAuditAssignment } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditAssignment'

const props = withDefaults(defineProps<{ embedded?: boolean }>(), { embedded: false })
const emit = defineEmits<{ close: [] }>()

const {
  isEdit,
  currentAudit,
  validationErrors,
  onStartDateSave,
  onEndDateSave,
  selectedDepartmentIds,
  onDepartmentsUpdate,
  auditGroupOptions,
  onAuditGroupSelect,
  onAuditFormSubmit,
  hasFormChanges,
  isEmployeeInfoModalOpen,
  selectedEmployeeId,
  // showEmployeeInfoModal,
  addAuditParticipant,
  addAuditParticipants,
  copyPointsToOthers,
  removeAuditParticipant,
  updateParticipantPoints,
  transferPoints,
  setManager,
  clearManager,
} = useAuditForm()

const auditGroupStore = useAuditGroupStore()
const auditRegisterStore = useAuditRegisterStore()
const feedbackStore = useFeedbackStore()
const apiStore = useApiStore()
const { xs } = useDisplay()

const assignmentExpandedState = ref<Record<string | number, boolean>>({})

// Ref на VForm для замера ширины — используется для ограничения ширины
// выпадашки шаблона группы, чтобы она не растягивалась под длинные названия.
const auditFormRef = ref<{ $el: HTMLElement } | null>(null)
const auditFormEl = computed(() => auditFormRef.value?.$el ?? null)

/**
 * Inline-стиль overlay выпадашек (шаблон группы / пункты участника):
 * Vuetify по умолчанию ставит overlay под левым краем активатора. Через
 * content-props перекрываем left и width на границы формы, чтобы overlay
 * был ровно по ширине формы и выровнен по её левому краю.
 * `!important` нужен, так как Vuetify пишет left/width в inline-style оверлея.
 */
const groupMenuContentProps = computed(() => {
  const form = auditFormEl.value?.getBoundingClientRect()
  if (!form) return {}

  return {
    style: `left: ${form.left}px !important; width: ${form.width}px !important; min-width: 0 !important; max-width: none !important;`,
  }
})
const route = useRoute()

const visible = ref(true)
const showUnsavedChangesDialog = ref(false)

// Refs для модальных окон выбора сотрудников
const participantModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)
const managerModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

// Диалог подтверждения удаления участника
const showDeleteParticipantDialog = ref(false)
const deleteParticipantId = ref<string>('')

const confirmDeleteParticipant = (employeeId: string) => {
  deleteParticipantId.value = employeeId
  showDeleteParticipantDialog.value = true
}

const onDeleteParticipantConfirm = (confirmed: boolean) => {
  showDeleteParticipantDialog.value = false
  if (confirmed && deleteParticipantId.value) {
    removeAuditParticipant(deleteParticipantId.value)
  }
  deleteParticipantId.value = ''
}

// Диалог передачи критериев
const showTransferDialog = ref(false)
const transferFromAssignment = ref<IAuditAssignment | null>(null)
const transferSelectedPointIds = ref<string[]>([])
const transferTargetEmployeeId = ref<string>('')
const transferTargetError = ref(false)

const actionButtonText = computed(() => route.params.id ? 'Сохранить' : 'Создать')

const canEditField = (fieldName: string): boolean => {
  if (!isEdit.value) return true

  return currentAudit.value.editable_fields?.includes(fieldName) ?? false
}

const canEditAssignment = (assignment: IAuditAssignment): boolean => {
  if (!canEditField('audit_assignments')) return false
  if (assignment.can_edit === false) return false

  return true
}

const isAddParticipantDisabled = computed(() => {
  if (!canEditField('audit_assignments')) return true
  if (isEdit.value && currentAudit.value?.status !== 'waiting') return true

  return false
})

const handleClose = () => {
  if (hasFormChanges()) {
    showUnsavedChangesDialog.value = true
    return
  }
  visible.value = false
  if (props.embedded) {
    emit('close')
  } else {
    feedbackStore.goToParentRoute()
  }
}

const onDialogDismissAttempt = useDialogDismissGuard(() => handleClose())

const handleCloseConfirm = async (value: boolean) => {
  showUnsavedChangesDialog.value = false
  if (value) {
    visible.value = false
    if (props.embedded) {
      emit('close')
    } else {
      await feedbackStore.goToParentRoute()
    }
  }
}

const cardTitle = computed(() => {
  if (isEdit.value && currentAudit.value.audit_type === 'planned') return 'Редактирование плановой проверки'
  if (isEdit.value && currentAudit.value.audit_type === 'unplanned') return 'Редактирование внеплановой проверки'
  return isEdit.value ? 'Загрузка...' : 'Создание проверки'
})

/**
 * Минимальная дата для пикера в поле «дата окончания».
 * Не раньше выбранной даты начала, в остальных случаях без ограничений.
 */
const minEndDate = computed<string | null>(() => {
  const start = currentAudit.value.planned_start
  if (typeof start === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(start)) return start
  return null
})

const isStartDateInPast = computed(() => {
  if (isEdit.value) return false

  const start = currentAudit.value.planned_start
  const startStr = typeof start === 'string' ? start : toApiDate(start)
  const today = toApiDate(new Date())

  return !!(startStr && today && startStr < today)
})

/**
 * Адаптеры для DateMaskInput: компонент эмитит YYYY-MM-DD | null.
 * onStartDateSave/onEndDateSave из composable не сбрасывают значение при null
 * (там `if (!dateValue) return`), поэтому очистку поля выполняем здесь —
 * пишем в стор пустую строку (формат emptyAudit). Для непустой даты
 * передаём управление в onStartDateSave/onEndDateSave, чтобы сохранить
 * бизнес-валидацию (сравнение начала и конца, тосты).
 */
const onStartFieldChange = (value: string | null) => {
  if (value === null) {
    currentAudit.value.planned_start = ''
    return
  }

  validationErrors.value.planned_start = false
  onStartDateSave(value)
}

const onEndFieldChange = (value: string | null) => {
  if (value === null) {
    currentAudit.value.planned_end = ''
    return
  }
  onEndDateSave(value)
}

const onStartDateInvalid = (message: string) => {
  currentAudit.value.planned_start = ''
  validationErrors.value.planned_start = true
  feedbackStore.showToast({
    type: 'error',
    message: `План. дата начала: ${message.toLowerCase()}`,
  })
}

const onEndDateInvalid = (message: string) => {
  feedbackStore.showToast({ type: 'error', message: `План. дата окончания: ${message.toLowerCase()}`, time: 5000 })
}

/**
 * Отображаемое имя руководителя
 */
const managerDisplayName = computed(() => {
  const m = currentAudit.value.manager
  if (!m?.id) return ''
  return getFullName(m)
})

/**
 * Список выбранных участников аудита для модального окна выбора
 */
const currentAuditSelectedEmployees = computed(() => {
  return currentAudit.value.audit_assignments.map(a => ({
    id: a.employee.id,
    full_name: getFullName(a.employee),
  } as IEmployeeTreeItem))
})

/**
 * Обработчик множественного выбора участников из модалки
 */
const onMultipleParticipantsSelected = (employees: IEmployeeTreeItem[]) => {
  addAuditParticipants(employees)
}

/**
 * Обработчик единичного выбора участника из модалки (обратная совместимость)
 */
const onParticipantSelected = (employee: IEmployeeTreeItem) => {
  addAuditParticipant(employee)
}

const getEmployeePosition = (assignment: IAuditAssignment) => {
  if (assignment.employee?.position_name) {
    return assignment.employee.position_name
  }

  const targetId = assignment.employee?.id
  if (!targetId) return ''

  if (apiStore.employees) {
    const findEmployee = (depts: any[]): any => {
      for (const dept of depts) {
        if (dept.employees) {
          const emp = dept.employees.find((e: any) => e.id === targetId)
          if (emp) return emp
        }
        if (dept.children) {
          const emp = findEmployee(dept.children)
          if (emp) return emp
        }
      }
      return null
    }
    const emp = findEmployee(apiStore.employees)
    if (emp) {
      return emp.position_name || emp.position?.name || ''
    }
  }
  return ''
}

const copyPointsBtnColor = (points?: { id: string; label?: string }[]) => {
  return points?.length ? 'primary' : 'text-primary'
}

/**
 * Обработчик выбора руководителя из модалки
 */
const onManagerSelected = (employee: IEmployeeTreeItem) => {
  setManager(employee)
}

/**
 * Открыть диалог передачи критериев
 */
const openTransferDialog = (assignment: IAuditAssignment) => {
  transferFromAssignment.value = assignment
  transferSelectedPointIds.value = []
  transferTargetEmployeeId.value = ''
  transferTargetError.value = false
  showTransferDialog.value = true
}

/**
 * Доступные участники для передачи (все кроме текущего)
 */
const transferTargetOptions = computed(() => {
  if (!transferFromAssignment.value) return []
  return currentAudit.value.audit_assignments
    .filter(a => a.employee.id !== transferFromAssignment.value?.employee.id)
    .map(a => ({
      title: getFullName(a.employee),
      value: a.employee.id,
    }))
})

const isAllPointsSelected = computed(() => {
  if (!transferFromAssignment.value?.points.length) return false
  return transferFromAssignment.value.points.every(p => transferSelectedPointIds.value.includes(p.id))
})

const toggleAllPoints = (val: boolean | null) => {
  if (!transferFromAssignment.value) return
  if (val) {
    transferSelectedPointIds.value = transferFromAssignment.value.points.map(p => p.id)
  } else {
    transferSelectedPointIds.value = []
  }
}

/**
 * Выполнить передачу критериев
 */
const executeTransfer = () => {
  if (!transferFromAssignment.value || !transferSelectedPointIds.value.length) return

  if (!transferTargetEmployeeId.value) {
    transferTargetError.value = true
    feedbackStore.showToast({ type: 'error', message: 'Выберите кому передать пункты', time: 10000 })
    return
  }

  transferPoints(
    transferFromAssignment.value.employee.id,
    transferTargetEmployeeId.value,
    transferSelectedPointIds.value,
  )

  showTransferDialog.value = false
  transferFromAssignment.value = null
}

const handleSubmit = async () => {
  const success = await onAuditFormSubmit()
  if (success && props.embedded) {
    visible.value = false
    emit('close')
  }
}

const addParticipantButtonText = computed(() => 'Выбрать аудиторов')

/* Режим выбора участников при создании: вручную (EmployeeSelectionModal) или из шаблона-группы */
const participantPickMode = ref<'manual' | 'template'>('manual')
const isAuditGroupMenuOpen = ref(false)

const auditGroupSelectItems = computed(() =>
  auditGroupOptions.value.map(g => ({ id: g.value, label: g.title })),
)

const onAuditGroupMenuSelect = async (id: string | number) => {
  isAuditGroupMenuOpen.value = false
  await onAuditGroupSelect(id)
}

// Замена участника по клику на ФИО
const replacingParticipantIndex = ref<number | null>(null)
const replaceModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

const openReplaceParticipantModal = (index: number) => {
  const assignment = currentAudit.value.audit_assignments[index]
  if (assignment && !canEditAssignment(assignment)) return

  replacingParticipantIndex.value = index
  replaceModalRef.value?.open()
}

const onParticipantReplaced = (employee: IEmployeeTreeItem) => {
  if (replacingParticipantIndex.value === null) return
  const assignment = currentAudit.value.audit_assignments[replacingParticipantIndex.value]
  if (!assignment) return

  // Проверяем что такой сотрудник ещё не добавлен
  const isDuplicate = currentAudit.value.audit_assignments.some(
    (a, i) => a.employee.id === employee.id && i !== replacingParticipantIndex.value,
  )
  if (isDuplicate) {
    feedbackStore.showToast({ type: 'error', message: 'Этот сотрудник уже добавлен как участник', time: 5000 })
    replacingParticipantIndex.value = null
    return
  }

  const parsed = parseFullName(employee.full_name)
  assignment.employee = {
    id: employee.id,
    first_name: parsed.first_name,
    last_name: parsed.last_name,
    middle_name: parsed.middle_name,
  }
  replacingParticipantIndex.value = null
}

// Состояние меню критериев для каждого участника (по employeeId)
const pointsMenuOpen = ref<Record<string, boolean>>({})

const onPointsChange = (
  employeeId: string,
  items: { id: string | number; label: string }[],
) => {
  const assignment = currentAudit.value.audit_assignments.find(a => a.employee.id === employeeId)
  if (assignment && !canEditAssignment(assignment)) return

  updateParticipantPoints(employeeId, items as { id: string; label: string }[])
}

const getSelectedPointIds = (assignment: IAuditAssignment) => {
  return assignment.points.map(p => p.id)
}

onMounted(async () => {
  auditRegisterStore.clearTreeChoices('audit_criterion')
  await auditGroupStore.fetchAuditsGroups()
  await apiStore.fetchAllDepartments()
})
</script>

<template>
  <VDialog
    v-model="visible"
    max-width="1000"
    content-class="entity-form-create-dialog"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard>
      <VCardTitle class="d-flex pa-6" :class="xs ? 'px-6 py-4' : 'pa-6'">
        <div class="form_card d-flex justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4"> {{ cardTitle }}</span>
          </div>
        </div>

        <CustomCloseButton :close-action="handleClose" />
      </VCardTitle>

      <VForm ref="auditFormRef" @submit="onAuditFormSubmit">
        <div class="entity-form-scroll-body d-flex flex-column ga-5 pt-2">
            <!-- Название аудита -->
          <VTextField
            v-model="currentAudit.name"
            :disabled="!canEditField('name')"
            :error="validationErrors.name"
            variant="outlined"
            hide-details
          >
            <template #label>
              <span>Тема проверки</span>
            </template>
          </VTextField>

          <!--
            Даты: ручной ввод по маске + popup VDatePicker по иконке календаря.
            DateMaskInput эмитит YYYY-MM-DD | null, что совпадает с форматом,
            который ждёт бекенд (см. toApiDate в utils/formatters.ts).
          -->
          <div class="d-flex flex-column w-100 ga-2">
            <div class="d-flex flex-column flex-sm-row align-stretch align-sm-center w-100 ga-3">
              <div class="w-100">
                <DateMaskInput
                  :model-value="currentAudit.planned_start"
                  :disabled="!canEditField('planned_start')"
                  :error="validationErrors.planned_start"
                  :persistent-placeholder="false"
                  @update:model-value="onStartFieldChange"
                  @invalid="onStartDateInvalid"
                >
                  <template #label>
                    <span>План. дата начала</span>
                  </template>
                </DateMaskInput>
              </div>

              <div class="w-100">
                <DateMaskInput
                  :model-value="currentAudit.planned_end"
                  :disabled="!canEditField('planned_end')"
                  :error="validationErrors.planned_end"
                  :min-date="minEndDate"
                  :persistent-placeholder="false"
                  @update:model-value="onEndFieldChange"
                  @invalid="onEndDateInvalid"
                >
                  <template #label>
                    <span>План. дата окончания</span>
                  </template>
                </DateMaskInput>
              </div>
            </div>

            <div
              v-if="isStartDateInPast"
              class="d-flex align-center ga-2 text-error"
            >
              <VIcon icon="ri-alert-line" size="16" color="error" />
              <span>Внимание! Дата начала аудита указана раньше текущей даты.</span>
            </div>
          </div>

          <!-- Отделения -->
          <NewTreeMultiSelectComponent
            label="Отделения"
            search-placeholder="Поиск отделения..."
            :persistent-placeholder="false"
            :tree-data="apiStore.departmentsTree"
            :search-tree="searchDepartments"
            :selected="selectedDepartmentIds"
            :disabled="!canEditField('departments')"
            :error="validationErrors.departments"
            @update:selected="onDepartmentsUpdate"
          />

          <!-- Руководитель аудита -->
          <VTextField
            :model-value="managerDisplayName"
            :disabled="!canEditField('manager')"
            :error="validationErrors.manager"
            class="invisible-readonly cursor-pointer"
            variant="outlined"
            readonly
            clearable
            @click="managerModalRef?.open()"
            @click:clear="clearManager"
          >
            <template #label>
              <span>Руководитель аудита</span>
            </template>
          </VTextField>

          <!-- Участники аудита -->
          <div class="d-flex flex-column ga-5">
            <div class="d-flex flex-column ga-4">
              <VRadioGroup
                v-model="participantPickMode"
                inline
                hide-details
                density="compact"
                color="primary"
                class="flex-grow-0"
              >
                <VRadio
                  label="Вручную"
                  value="manual"
                />
                <VRadio
                  label="Использовать группу"
                  value="template"
                  class="ms-5"
                />
              </VRadioGroup>

              <!-- «Использовать группу»: выпадашка групп -->
              <VMenu
                v-if="participantPickMode === 'template'"
                v-model="isAuditGroupMenuOpen"
                :close-on-content-click="false"
                location="bottom"
                :content-props="groupMenuContentProps"
                content-class="entity-form-menu"
              >
                <template #activator="{ props: menuProps }">
                  <div v-bind="menuProps" class="d-flex">
                    <VBtn
                      :class="{ 'flex-grow-1': xs }"
                      :disabled="isAddParticipantDisabled"
                      variant="outlined"
                      prepend-icon="ri-user-add-line"
                      class="rounded-te-0 rounded-be-0"
                      :error="validationErrors.audit_assignments"
                    >
                      {{ addParticipantButtonText }}
                    </VBtn>
                    <VIconBtn
                      color="primary"
                      :disabled="isAddParticipantDisabled"
                      class="rounded-ts-0 rounded-ds-0 rounded-te rounded-be participants-split-end"
                      icon="ri-arrow-down-s-line"
                      variant="outlined"
                      :rounded="false"
                      size="38"
                    />
                  </div>
                </template>

                <SearchableSelectList
                  :items="auditGroupSelectItems"
                  placeholder="Поиск группы..."
                  @select="item => onAuditGroupMenuSelect(item.id)"
                />
              </VMenu>

              <!-- Вручную / редактирование: EmployeeSelectionModal -->
              <div
                v-else
                class="d-flex"
              >
                <VBtn
                  :class="{ 'flex-grow-1': xs }"
                  :disabled="isAddParticipantDisabled"
                  variant="outlined"
                  prepend-icon="ri-user-add-line"
                  class="rounded-te-0 rounded-be-0"
                  :error="validationErrors.audit_assignments"
                  @click="participantModalRef?.open()"
                >
                  {{ addParticipantButtonText }}
                </VBtn>
                <VIconBtn
                  color="primary"
                  :disabled="isAddParticipantDisabled"
                  class="rounded-ts-0 rounded-ds-0 rounded-te rounded-be participants-split-end"
                  icon="ri-arrow-down-s-line"
                  variant="outlined"
                  :rounded="false"
                  size="38"
                  @click="participantModalRef?.open()"
                />
              </div>
            </div>

            <!-- Список участников -->
            <template
              v-for="(assignment, index) in currentAudit.audit_assignments"
              :key="assignment.employee.id"
            >
              <!-- Разделитель между участниками на мобильном -->
              <CustomDivider v-if="xs && index > 0" />

              <div class="d-flex w-100" :class="xs ? 'flex-column gap-4' : 'align-center gap-2'">
                <!-- Мобильная шапка с кнопками действий -->
                <div
                  v-if="xs"
                  class="d-flex justify-space-between align-center w-100"
                >
                  <span class="text-secondary font-weight-medium">Аудитор</span>
                  <div class="d-flex align-center ga-3 ml-auto">
                    <VTooltip
                      location="top"
                      text="Скопировать предмет проверки во все поля"
                    >
                      <template #activator="{ props: tooltipProps }">
                        <span v-bind="tooltipProps">
                          <VIconBtn
                            :rounded="false"
                            :size="34"
                            class="rounded-sm flex-grow-0 flex-shrink-0 copy-points-btn"
                            :color="copyPointsBtnColor(assignment.points)"
                            icon="ri-checkbox-multiple-blank-line"
                            variant="text"
                            :disabled="!canEditAssignment(assignment) || !assignment.points?.length"
                            @click="copyPointsToOthers(assignment.employee.id)"
                          />
                        </span>
                      </template>
                    </VTooltip>
                    <VIconBtn
                      :rounded="false"
                      :size="34"
                      class="rounded-sm flex-grow-0 flex-shrink-0"
                      color="secondary"
                      icon="ri-delete-bin-3-line"
                      variant="text"
                      :disabled="!canEditAssignment(assignment)"
                      @click="confirmDeleteParticipant(assignment.employee.id)"
                    />
                  </div>
                </div>

                <!-- ФИО -->
                <VTextField
                  :model-value="getFullName(assignment.employee)"
                  :disabled="!canEditAssignment(assignment)"
                  readonly
                  variant="outlined"
                  persistent-placeholder
                  label="ФИО"
                  class="flex-1-1-0 min-w-0 invisible-readonly"
                  :class="canEditAssignment(assignment) ? 'cursor-pointer' : 'cursor-default'"
                  @click="canEditAssignment(assignment) ? openReplaceParticipantModal(index) : undefined"
                />

                <!-- Должность -->
                <VTextField
                  :model-value="getEmployeePosition(assignment)"
                  readonly
                  class="flex-1-1-0 min-w-0"
                  label="Должность"
                  variant="outlined"
                  persistent-placeholder
                  :class="xs ? 'w-100' : 'max-width-250'"
                />

                <!-- Предмет проверки -->
                <div class="flex-1-1-0 min-w-0">
                  <div
                    class="v-field v-field--active v-field--variant-outlined audit-points-field"
                    :class="{
                      'audit-points-field--collapsed': !assignmentExpandedState[assignment.employee.id],
                      'cursor-pointer': canEditAssignment(assignment),
                      'audit-points-field--disabled': !canEditAssignment(assignment),
                    }"
                    @click="canEditAssignment(assignment) ? (pointsMenuOpen[assignment.employee.id] = true) : undefined"
                  >
                    <div
                      class="v-field__field"
                      :class="assignmentExpandedState[assignment.employee.id] ? 'pa-3' : 'd-flex align-center px-4 overflow-hidden'"
                    >
                      <CollapsibleCell
                        v-if="assignment.points?.length"
                        v-model:is-expanded="assignmentExpandedState[assignment.employee.id]"
                        :items="assignment.points"
                        type-label="предметов проверки"
                        :collapsed-items-count="5"
                      />
                    </div>
                    <div class="v-field__append-inner d-flex align-center h-100 ga-1 pe-3 pt-0">
                      <VIcon
                        v-if="assignment.points?.length && canEditAssignment(assignment)"
                        icon="ri-close-line"
                        size="18"
                        class="text-medium-emphasis cursor-pointer"
                        @click.stop="onPointsChange(assignment.employee.id, [])"
                      />
                      <VIcon
                        v-if="canEditAssignment(assignment)"
                        :icon="pointsMenuOpen[assignment.employee.id] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                        size="20"
                        class="text-medium-emphasis"
                      />
                    </div>
                    <div class="v-field__outline">
                      <div class="v-field__outline__start"></div>
                      <div class="v-field__outline__notch">
                        <label class="v-label v-field-label v-field-label--floating">Предмет проверки</label>
                      </div>
                      <div class="v-field__outline__end"></div>
                    </div>
                  </div>
                </div>

                <VDialog
                  v-if="canEditAssignment(assignment)"
                  v-model="pointsMenuOpen[assignment.employee.id]"
                  max-width="1100"
                  max-height="90vh"
                  content-class="entity-form-menu"
                >
                  <UniversalTreeMultiSelect
                    field-key="audit_criterion"
                    module="audit-criterion"
                    :store="auditRegisterStore"
                    only-leaves
                    collapsed-by-default
                    :model-value="getSelectedPointIds(assignment)"
                    @change="(items) => onPointsChange(assignment.employee.id, items)"
                    @close="pointsMenuOpen[assignment.employee.id] = false"
                  />
                </VDialog>

                <!-- Кнопки действий на десктопе -->
                <div
                  v-if="!xs"
                  class="d-flex align-center ga-3 flex-grow-0 flex-shrink-0"
                >
                  <!-- Кнопка копирования критериев во все поля -->
                  <VTooltip location="top" text="Скопировать предмет проверки во все поля">
                    <template #activator="{ props: tooltipProps }">
                      <span v-bind="tooltipProps">
                        <VIconBtn
                          :rounded="false"
                          :size="34"
                          class="rounded-sm flex-grow-0 flex-shrink-0 copy-points-btn"
                          :color="copyPointsBtnColor(assignment.points)"
                          icon="ri-checkbox-multiple-blank-line"
                          variant="text"
                          :disabled="!canEditAssignment(assignment) || !assignment.points?.length"
                          @click="copyPointsToOthers(assignment.employee.id)"
                        />
                      </span>
                    </template>
                  </VTooltip>

                  <!-- Кнопка удаления участника -->
                  <VIconBtn
                    :rounded="false"
                    :size="34"
                    class="rounded-sm flex-grow-0 flex-shrink-0"
                    color="secondary"
                    icon="ri-delete-bin-3-line"
                    variant="text"
                    :disabled="!canEditAssignment(assignment)"
                    @click="confirmDeleteParticipant(assignment.employee.id)"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>

        <footer class="d-flex" :class="xs ? 'flex-column' : 'justify-end align-center'">
          <VBtn :block="xs" @click="handleSubmit">
            {{ actionButtonText }}
          </VBtn>
        </footer>
      </VForm>
    </VCard>
  </VDialog>

  <ConfirmDialog
    v-model:is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Процесс будет прерван, данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отменить"
    @confirm="handleCloseConfirm"
  />

  <ConfirmDialog
    v-model:is-dialog-visible="showDeleteParticipantDialog"
    confirmation-question="Удалить участника?"
    confirmation-text="Участник будет удалён из аудита"
    confirm-btn="Удалить"
    reject-btn="Отменить"
    @confirm="onDeleteParticipantConfirm"
  />

  <EmployeeProfileSidebar
    v-model="isEmployeeInfoModalOpen"
    :employee-id="selectedEmployeeId"
  />

  <!-- Модалка выбора участника -->
  <EmployeeSelectionModal
    ref="participantModalRef"
    :employees="apiStore.employees"
    multiple
    :selected-employees="currentAuditSelectedEmployees"
    @select-multiple="onMultipleParticipantsSelected"
  />

  <!-- Модалка выбора руководителя -->
  <EmployeeSelectionModal
    ref="managerModalRef"
    :employees="apiStore.employees"
    title="Выберите руководителя аудита"
    @select="onManagerSelected"
  />

  <!-- Модалка замены участника -->
  <EmployeeSelectionModal
    ref="replaceModalRef"
    :employees="apiStore.employees"
    title="Заменить участника"
    @select="onParticipantReplaced"
  />

  <!-- Диалог передачи критериев -->
  <VDialog v-model="showTransferDialog" max-width="500">
    <VCard>
      <VCardTitle class="d-flex justify-space-between align-center pa-4">
        <span>Передать пункты</span>
        <CustomCloseButton :close-action="() => showTransferDialog = false" />
      </VCardTitle>

      <VCardText class="d-flex flex-column gap-4">
        <div class="text-body-2 text-medium-emphasis">
          От: {{ transferFromAssignment ? getFullName(transferFromAssignment.employee) : '' }}
        </div>

        <!-- Список критериев с чекбоксами -->
        <div v-if="transferFromAssignment" class="d-flex flex-column gap-2">
          <VCheckbox
            :model-value="isAllPointsSelected"
            label="Все"
            hide-details
            density="compact"
            @update:model-value="toggleAllPoints"
          />
          <VDivider class="my-1" />
          <VCheckbox
            v-for="point in transferFromAssignment.points"
            :key="point.id"
            :model-value="transferSelectedPointIds.includes(point.id)"
            :label="point.label || point.id"
            hide-details
            density="compact"
            @update:model-value="(val: boolean | null) => {
              if (val) transferSelectedPointIds.push(point.id)
              else transferSelectedPointIds = transferSelectedPointIds.filter(id => id !== point.id)
            }"
          />
        </div>

        <!-- Выбор целевого участника -->
        <VSelect
          v-model="transferTargetEmployeeId"
          :items="transferTargetOptions"
          item-title="title"
          item-value="value"
          label="Кому передать"
          variant="outlined"
          hide-details
          :error="transferTargetError"
          :menu-props="{ contentClass: 'entity-form-menu' }"
          @update:model-value="transferTargetError = false"
        />

        <div class="d-flex justify-end gap-2">
          <VBtn variant="outlined" color="secondary" @click="showTransferDialog = false">
            Отмена
          </VBtn>
          <VBtn
            :disabled="!transferSelectedPointIds.length"
            @click="executeTransfer"
          >
            Передать
          </VBtn>
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.min-w-0 {
  min-width: 0;
}

.responsibility-table {
  width: 100%;
  border-collapse: collapse;
}

.responsibility-table thead th {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  position: relative;
  white-space: nowrap;
  text-align: left;
  padding: 12px;
}

.responsibility-table thead th:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

.responsibility-table tbody td {
  padding: 12px;
  vertical-align: top;
}

/* КОЛОНКИ */

/* Критерий */
.responsibility-table thead th:nth-child(1),
.responsibility-table tbody td:nth-child(1) {
  min-width: 15rem;
}

/* Аудитор */
.responsibility-table thead th:nth-child(2),
.responsibility-table tbody td:nth-child(2) {
  min-width: 11.25rem;
}

/* VIconBtn outlined даёт color: inherit — форсим primary как у соседней VBtn (как в AddMultipleParticipants) */
.participants-split-end {
  color: rgb(var(--v-theme-primary)) !important;
  border-color: rgb(var(--v-theme-primary)) !important;

  :deep(.v-icon) {
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.v-icon-btn--disabled {
    opacity: 0.45;
  }
}

.max-width-250 {
  max-width: 250px !important;
}

/* Disabled: text-primary @ 30%, без глобального 0.45 у .v-icon-btn--disabled */
.copy-points-btn.v-icon-btn--disabled,
.copy-points-btn.v-btn--disabled {
  opacity: 1;
  color: rgb(var(--v-theme-text-primary), 0.3);
}

/* Контент не должен раздувать flex-basis — иначе «Предмет проверки» сжимает ФИО и должность */
.audit-points-field {
  min-width: 0;
  min-block-size: 48px;
}

.audit-points-field--disabled {
  background: rgb(var(--v-theme-grey-50)) !important;
  cursor: default !important;

  .v-label {
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  }

  .v-field__outline {
    opacity: var(--v-disabled-opacity, 0.38);
  }
}

.audit-points-field--collapsed {
  :deep(.cell-wrap) {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    min-width: 0;
    overflow: hidden;
  }

  :deep(.cell-codes) {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :deep(.cell-expand-btn) {
    flex-shrink: 0;
  }
}
</style>
